import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCardFrontImageSrc } from '../lib/cardImages';
import type { Orientation, TarotCard } from '../lib/types';

type RitualCard = {
  card: TarotCard;
  orientation: Orientation;
  label: string;
};

type RitualStep = {
  id: string;
  stage: string;
  title: string;
  narration: string;
  prompt: string;
  cue: string;
  revealCards?: number;
};

const CARDS: RitualCard[] = [
  {
    card: {
      id: 'cups-09',
      name_en: 'Nine of Cups',
      name_kr: '컵 9',
      arcana: 'minor',
      suit: 'cups',
      number: 9,
      keywords_upright: ['만족', '바람의 성취', '정서적 충만'],
      keywords_reversed: ['공허한 만족', '과잉 기대', '속마음의 허전함'],
      meaning_short: '원하던 장면이 눈앞에 왔는데도 마음이 다 차지 않는 순간',
    },
    orientation: 'upright',
    label: '표면에 드러난 욕구',
  },
  {
    card: {
      id: 'major-18',
      name_en: 'The Moon',
      name_kr: '달',
      arcana: 'major',
      number: 18,
      keywords_upright: ['불안', '직감', '감춰진 흐름'],
      keywords_reversed: ['혼란의 정리', '환상에서 깨어남', '불안의 실체화'],
      meaning_short: '말로 설명하기 어려운 불안과 직감이 동시에 떠오르는 카드',
    },
    orientation: 'upright',
    label: '아직 말로 꺼내지 못한 본심',
  },
  {
    card: {
      id: 'pentacles-08',
      name_en: 'Eight of Pentacles',
      name_kr: '펜타클 8',
      arcana: 'minor',
      suit: 'pentacles',
      number: 8,
      keywords_upright: ['정교한 반복', '실력 쌓기', '현실적인 집중'],
      keywords_reversed: ['의미 없는 반복', '집중력 저하', '노력의 분산'],
      meaning_short: '감정 대신 손을 움직이며 기준을 다시 세우는 카드',
    },
    orientation: 'upright',
    label: '현실로 내려오는 행동선',
  },
];

const STEPS: RitualStep[] = [
  {
    id: 'settle',
    stage: '01',
    title: '먼저 질문보다 표정을 본다',
    narration:
      '몰입감 있는 리딩은 질문을 바로 해석하지 않는다. 먼저 사용자의 숨, 피로, 머뭇거림 같은 기류를 읽어야 한다. 화면에서도 그 준비 시간을 허락해야 세션처럼 느껴진다.',
    prompt: '지금 가장 먼저 떠오르는 감정은 기대, 조급함, 불안 중 어디에 가까운가?',
    cue: 'tarotist cue: 첫 장을 펼치기 전, 손의 긴장을 천천히 풀게 만든다.',
    revealCards: 0,
  },
  {
    id: 'name',
    stage: '02',
    title: '질문의 진짜 이름을 다시 묻는다',
    narration:
      '표면 질문이 “이 선택이 잘될까?”여도, 바닥 질문은 “내가 틀렸다는 걸 확인하게 될까?”인 경우가 많다. 여기서 리더는 사용자의 문장을 더 정확한 감정 언어로 바꿔준다.',
    prompt: '네가 두려운 건 결과 자체인가, 결과 이후의 책임인가?',
    cue: 'ritual step: 질문 문장을 다시 읽고, 그 안에 숨어 있는 감정 명사를 하나 고르게 한다.',
    revealCards: 0,
  },
  {
    id: 'surface',
    stage: '03',
    title: '첫 카드가 보여준 겉면의 욕구',
    narration:
      '컵 9가 먼저 나온 건, 겉으로는 이미 원하는 그림이 명확하다는 뜻이다. 사용자는 무엇을 얻고 싶은지 알고 있다. 하지만 이 카드는 만족의 그림자까지 같이 데려온다. 그래서 리더는 “원한다”보다 “왜 끝까지 안심이 안 되는가”를 바로 본다.',
    prompt: '원하는 장면이 손에 들어와도 마음이 쉽게 안 놓이는 이유는 무엇인가?',
    cue: 'reader note: 첫 카드는 결과가 아니라 갈망의 표면을 읽는다.',
    revealCards: 1,
  },
  {
    id: 'core',
    stage: '04',
    title: '두 번째 카드가 찌르는 본심',
    narration:
      '달이 이어지는 순간, 리딩은 분위기가 바뀐다. 이건 카드 뜻을 설명하는 장면이 아니라, 사용자가 말로 다 못 한 불안을 리더가 대신 짚어주는 장면이다. 앞 카드의 욕구가 여기서 불안의 이유를 얻는다.',
    prompt: '말로 설명하기 어렵지만 반복해서 떠오르는 최악의 장면은 무엇인가?',
    cue: 'tarotist cue: 앞 카드의 욕구와 이번 카드의 불안을 같은 문장 안에서 묶는다.',
    revealCards: 2,
  },
  {
    id: 'landing',
    stage: '05',
    title: '마지막 카드는 현실의 손동작으로 내려온다',
    narration:
      '펜타클 8은 감정을 멈추게 만드는 카드가 아니라, 감정을 다루는 방식을 현실의 루틴으로 옮기게 만드는 카드다. 세션의 마지막은 위로가 아니라, 오늘 당장 손댈 수 있는 기준을 하나 남기는 쪽이 더 강하다.',
    prompt: '이번 주 안에 숫자나 시간으로 점검할 수 있는 행동 하나를 고른다면 무엇인가?',
    cue: 'closing cue: 행동 하나만 남기고, 그 행동이 왜 불안을 줄이는지 설명한다.',
    revealCards: 3,
  },
];

export default function RitualJourneyLabPage() {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = STEPS[currentStepIndex];

  const visibleCards = useMemo(() => CARDS.slice(0, currentStep.revealCards ?? 0), [currentStep.revealCards]);

  return (
    <main className="min-h-screen bg-[#07060a] text-[#f7efe4]">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_10%_10%,_rgba(194,147,91,0.14),_transparent_30%),radial-gradient(circle_at_90%_15%,_rgba(126,76,148,0.18),_transparent_22%),linear-gradient(180deg,_#110d11_0%,_#07060a_52%,_#07060a_100%)]" />

      <section className="mx-auto max-w-[1450px] px-5 pb-16 pt-20 lg:px-10">
        <div className="flex flex-col gap-4 border-b border-white/10 pb-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#c89b61]">Prototype 02 · Guided Ritual</p>
            <h1 className="mt-4 font-['Song_Myung'] text-4xl leading-[1.05] text-[#fff8ed] sm:text-5xl lg:text-6xl">
              질문을 풀기 전에
              <br />
              감정을 예열하는 리딩
            </h1>
            <p className="mt-5 text-sm leading-8 text-[#d7c9b3] sm:text-base">
              CHANI류의 가이드형 흐름에서 가져온 아이디어입니다. 질문을 던지자마자 정답을 말하지 않고,
              사용자가 스스로 감정과 질문의 진짜 이름을 정리하게 한 다음 카드를 열어 몰입을 올리는 방식입니다.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-[#d9ccb8]">
            <Link to="/lab" className="rounded-full border border-white/15 px-4 py-2 transition hover:border-[#d5a462] hover:text-white">
              실험실 홈
            </Link>
            <Link to="/lab/reader-styles" className="rounded-full bg-[#f5eee4] px-4 py-2 font-medium text-[#120d11] transition hover:bg-white">
              다음 프로토타입
            </Link>
          </div>
        </div>

        <div className="mt-8 grid gap-8 xl:grid-cols-[0.8fr_1.2fr_0.8fr]">
          <aside className="rounded-[30px] border border-white/10 bg-white/[0.03] p-6">
            <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Ritual Progress</p>
            <div className="mt-5 space-y-3">
              {STEPS.map((step, index) => {
                const state =
                  index === currentStepIndex ? 'current' : index < currentStepIndex ? 'done' : 'next';

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setCurrentStepIndex(index)}
                    className={`w-full rounded-[24px] border px-4 py-4 text-left transition ${
                      state === 'current'
                        ? 'border-[#d5a462]/50 bg-[#1a1417] text-white'
                        : state === 'done'
                          ? 'border-white/10 bg-white/[0.05] text-[#d8c8b4]'
                          : 'border-white/6 bg-black/10 text-[#a99b89]'
                    }`}
                  >
                    <span className="block text-[10px] uppercase tracking-[0.36em] text-[#c89b61]">{step.stage}</span>
                    <span className="mt-2 block text-lg font-medium">{step.title}</span>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="rounded-[30px] border border-white/10 bg-[#120d11]/75 p-6 shadow-[0_40px_110px_rgba(0,0,0,0.34)]">
            <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Current Step</p>
            <h2 className="mt-3 text-3xl font-semibold text-[#fff7eb]">{currentStep.title}</h2>
            <p className="mt-5 text-[15px] leading-8 text-[#e8dccb]">{currentStep.narration}</p>

            <div className="mt-6 rounded-[24px] border border-[#c89b61]/20 bg-[#1a1418] px-5 py-4">
              <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Prompt To The User</p>
              <p className="mt-3 text-sm leading-7 text-[#f1e7d8]">{currentStep.prompt}</p>
            </div>

            <div className="mt-4 rounded-[24px] border border-white/8 bg-white/[0.03] px-5 py-4">
              <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Reader Cue</p>
              <p className="mt-3 text-sm leading-7 text-[#d8c9b5]">{currentStep.cue}</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setCurrentStepIndex((index) => Math.max(index - 1, 0))}
                disabled={currentStepIndex === 0}
                className="rounded-full border border-white/12 px-4 py-2 text-sm text-[#f4ede2] transition hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-35"
              >
                이전 단계
              </button>
              <button
                type="button"
                onClick={() => setCurrentStepIndex((index) => Math.min(index + 1, STEPS.length - 1))}
                disabled={currentStepIndex === STEPS.length - 1}
                className="rounded-full bg-[#f5eee4] px-5 py-2 text-sm font-medium text-[#110d11] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                다음 단계
              </button>
            </div>
          </section>

          <aside className="rounded-[30px] border border-white/10 bg-white/[0.03] p-6">
            <p className="text-[11px] uppercase tracking-[0.34em] text-[#c89b61]">Card Reveal</p>
            <div className="mt-5 flex min-h-[420px] items-center justify-center rounded-[26px] border border-white/8 bg-black/10 px-4 py-6">
              {visibleCards.length === 0 ? (
                <p className="max-w-[240px] text-center text-sm leading-7 text-[#b9ab98]">
                  아직 카드를 열지 않았습니다. 질문의 감정선부터 정리하게 만들어, 카드가 등장하는 순간의 체감을 키우는 구조입니다.
                </p>
              ) : (
                <div className="relative h-[320px] w-[220px]">
                  {visibleCards.map((item, index) => (
                    <div
                      key={item.card.id}
                      className="absolute left-1/2 top-1/2 h-[300px] w-[200px] -translate-x-1/2 -translate-y-1/2"
                      style={{
                        transform: `translate(-50%, -50%) translate(${index * 18}px, ${index * 8}px) rotate(${index === 1 ? -6 : index === 2 ? 8 : -2}deg)`,
                        zIndex: 10 + index,
                      }}
                    >
                      <div className="h-full overflow-hidden rounded-[22px] border border-[#d5a462]/35 bg-[#120d11] shadow-[0_20px_70px_rgba(0,0,0,0.35)]">
                        <img
                          src={getCardFrontImageSrc(item.card)}
                          alt={item.card.name_kr}
                          className={`h-full w-full object-cover ${item.orientation === 'reversed' ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5 space-y-3">
              {CARDS.map((item, index) => (
                <article
                  key={item.card.id}
                  className={`rounded-2xl border px-4 py-4 transition ${
                    index < visibleCards.length ? 'border-[#d5a462]/30 bg-[#171116]' : 'border-white/8 bg-black/10'
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-[0.34em] text-[#c89b61]">{item.label}</p>
                  <p className="mt-2 text-lg font-semibold text-[#fff6e9]">{item.card.name_kr}</p>
                </article>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
